import { useEffect, useRef } from 'react';

import useToast from './useToast';

import { verificarNombre } from '@/services/verifyName';
import { TEXTS } from '@/utils/constants';
import { enviarEventoVerificacionNombre } from '@/utils/gtm';

const COINCIDENCIA_MINIMA = 0.7;

// localStorage.setItem('enableVerificarNombreDni', 'true') para habilitar
const isVerificarNombreEnabled =
  localStorage.getItem('enableVerificarNombreDni') !== 'false';

type VerificarNombreArgs = {
  dni: string;
  nombre: string;
  apellido: string;
  genero: 'M' | 'F' | '';
};

const useVerificarNombre = () => {
  const toast = useToast();
  const lastCheckedKey = useRef<string | null>(null);
  const hasMismatched = useRef(false);
  const requestId = useRef(0);
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  const checkNombre = async ({
    dni,
    nombre,
    apellido,
    genero,
  }: VerificarNombreArgs) => {
    if (!isVerificarNombreEnabled) return;

    const dniDigits = dni.replace(/\D/g, '');
    if (!dniDigits || !nombre.trim() || !apellido.trim() || !genero) return;

    const key = `${dniDigits}|${nombre}|${apellido}|${genero}`;
    if (key === lastCheckedKey.current) return;
    lastCheckedKey.current = key;

    const currentRequestId = ++requestId.current;

    try {
      const response = await verificarNombre({
        dni: dniDigits,
        nombreCompleto: `${nombre} ${apellido}`.trim(),
        genero,
      });

      // Chequeo obsoleto: ya se disparó uno más nuevo mientras esperábamos esta respuesta.
      if (currentRequestId !== requestId.current) return;

      if (!response.estado) return;

      if (response.cuilNoEncontrado) {
        if (!hasMismatched.current && isMounted.current) {
          toast.show(TEXTS.nombreDniMismatch);
        }
        enviarEventoVerificacionNombre('cuilNoEncontrado');
        hasMismatched.current = true;
        return;
      }

      if (typeof response.coincidencia !== 'number') return;

      if (response.coincidencia < COINCIDENCIA_MINIMA) {
        if (!hasMismatched.current && isMounted.current) {
          toast.show(TEXTS.nombreDniMismatch);
        }
        enviarEventoVerificacionNombre('coincidenciaBaja');
        hasMismatched.current = true;
        return;
      }

      if (hasMismatched.current) {
        enviarEventoVerificacionNombre('corregido');
        hasMismatched.current = false;
      }
    } catch (error) {
      // Chequeo best-effort: AFIP inestable no debe interrumpir la registración.
      console.error('Error al verificar nombre contra DNI', error);
    }
  };

  return { checkNombre };
};

export default useVerificarNombre;
